import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";
import Seo from "../components/Seo";
import { getDonanimPage } from "../services/api";
import { useLang } from "../context/LanguageContext";
import defaultDonanim from '../default_datas/pages/defaultDonanim';
 
const STRAPI_BASE = import.meta.env.VITE_STRAPI_URL || "";
 
const getStrapiImg = (field) => {
  if (!field) return null;

  // Strapi v5 — düz obje: { url: "https://..." }
  if (field.url) {
    return field.url.startsWith("http") ? field.url : `${STRAPI_BASE}${field.url}`;
  }

  // Strapi v4 — { data: { attributes: { url } } }
  const single = field?.data?.attributes?.url;
  if (single) return single.startsWith("http") ? single : `${STRAPI_BASE}${single}`;

  // Strapi v4 array — { data: [{ attributes: { url } }] }
  const first = field?.data?.[0]?.attributes?.url;
  if (first) return first.startsWith("http") ? first : `${STRAPI_BASE}${first}`;

  return null;
};
 
// Fallback görseller (Strapi'den resim gelene kadar kullanılır)
const HERO_FALLBACK = "https://lh3.googleusercontent.com/aida-public/AB6AXuA5g3mQI2eSq1QpoCTZhaQ4ygWwJgsZSVcV-t69I_rvwM8km-RBnt2PBGsueOcHMhQPDrJ5p8Z_jFJTF16QRiiY8WuD-BzVzTDb92RBG3cdQHpXOwIsqTAeOV4YoumH2FqudLNJyxIx3PhGWJAoFqpzjaZWFbXb-xKsapRlspSDziB4FMwE5BboOtLu2ExbJIifMdtR1LkMuVbrCR6B0kWKR-MhKxk_fE757HWfup12TKcpRQpVr6qF26qkOxYd1bir9aZBbsihz4M";
const GRID_FALLBACK = "https://lh3.googleusercontent.com/aida-public/AB6AXuAZXH5zCJ-KbEGde6h9Pcis01qnYLV31CaNnw9TGwnnYtt0apSEA-MApM3_V5hO5GwP0KqqXAx1oFSZ7ivP0qfIt1-6BupakPIsZ-Mb-ez65rOOs9uiTq1RVUbP7IVWWGzS5SqbzyLWALPLZ7qZUGV57E6G_iHC9GyydyRPTdANwmDru4HEIR6DV0dx2NtQ5Gq51hHlSbguuLhcDOccgXKKbZZmtL_32DktCZ5bhx6UweRydXxoThtOp0JC_MQrLmtKvUsD6wLGkPQ";
 
const ShimmerLoader = () => (
  <div className="animate-pulse space-y-8 p-10 bg-ruzy-light min-h-screen">
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div className="h-6 w-32 rounded-full bg-gray-200" />
          <div className="h-12 w-3/4 rounded bg-gray-200" />
          <div className="h-6 w-full rounded bg-gray-200" />
          <div className="flex gap-4">
            <div className="h-14 w-36 rounded-lg bg-gray-200" />
            <div className="h-14 w-36 rounded-lg bg-gray-200" />
          </div>
        </div>
        <div className="h-64 rounded-2xl bg-gray-200" />
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-48 rounded-2xl bg-gray-200" />
        ))}
      </div>
    </div>
  </div>
);
 
const Donanim = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [data, setData] = useState(defaultDonanim);
  const [loading, setLoading] = useState(true);
 
  useEffect(() => {
    getDonanimPage()
      .then((res) => {
        if (res && res.pageContent) {
          const hero = res.pageContent.find(s => s.__component === "sections.hero");
          const grid = res.pageContent.find(s => s.__component === "sections.grid-section");

          setData(res);
        }
      })
      .catch((err) => {
        console.error('❌ Strapi hatası — varsayılan veriler kullanılıyor:', err);
      })
      .finally(() => setLoading(false));
  }, []);
 
  const t = (section, field) => {
    return section?.[lang === "en" ? `${field}_en` : field] || section?.[field] || "";
  };
 
  const ti = (item, field) => {
    return item?.[lang === "en" ? `${field}_en` : field] || item?.[field] || "";
  };
 
  if (loading) return <ShimmerLoader />;
 
  const pageContent = data?.pageContent || [];
 
  return (
    <>
      <Seo
        title="Donanım | RUZY"
        description="RUZY platformunun desteklediği donanım çözümleri"
        path="/donanim"
      />
      <div className="relative flex min-h-screen w-full flex-col overflow-x-hidden bg-ruzy-light font-poppins text-ruzy-dark">
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-10 py-8 md:py-12">
          {pageContent.map((section, idx) => {
 
            // ===== 1. HERO =====
            if (section.__component === "sections.hero") {
              const heroImg = getStrapiImg(section.image)
              return (
                <ScrollReveal key={idx} delay={0}>
                  <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center mb-16 md:mb-24">
                    <div className="flex flex-col gap-6 md:gap-8 order-2 lg:order-1">
                      <div className="flex flex-col gap-4">
                        {t(section, "badge") && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-ruzy-accent/10 text-ruzy-accent w-fit uppercase tracking-wider">
                            {t(section, "badge")}
                          </span>
                        )}
                        {t(section, "title") && (
                          <h1 className="text-ruzy-dark text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
                            {t(section, "title")}
                          </h1>
                        )}
                        {t(section, "description") && (
                          <p className="text-ruzy-text text-lg md:text-xl leading-relaxed max-w-xl">
                            {t(section, "description")}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-4">
                        <button
                          onClick={() => navigate("/iletisim")}
                          className="flex min-w-[160px] items-center justify-center rounded-lg h-14 px-8 bg-ruzy-accent hover:bg-ruzy-hover text-white text-lg font-bold hover:shadow-lg transition-all"
                        >
                          {lang === "en" ? "Get Started" : "Hemen Başla"}
                        </button>
                        <button
                          onClick={() => navigate("/iletisim")}
                          className="flex min-w-[160px] items-center justify-center rounded-lg h-14 px-8 border-2 border-ruzy-border text-ruzy-text hover:bg-ruzy-light transition-all text-lg font-bold"
                        >
                          {lang === "en" ? "Watch Demo" : "Demoyu İzle"}
                        </button>
                      </div>
                    </div>
 
                    <div className="relative order-1 lg:order-2">
                      <div
                        className="w-full aspect-video bg-cover bg-center rounded-2xl shadow-2xl border-8 border-white"
                        style={heroImg ? { backgroundImage: `url("${heroImg}")` } : {}}
                      />
                    </div>
                  </section>
                </ScrollReveal>
              );
            }
 
            // ===== 2. FEATURES SECTION =====
            if (section.__component === "sections.features-section") {
              return (
                <ScrollReveal key={idx} delay={0.1}>
                  <section className="mb-20">
                    <div className="flex flex-col gap-4 mb-12">
                      {t(section, "Title") && (
                        <h2 className="text-ruzy-dark text-3xl md:text-4xl font-bold tracking-tight">
                          {t(section, "Title")}
                        </h2>
                      )}
                      {t(section, "description") && (
                        <p className="text-ruzy-text text-lg max-w-2xl">
                          {t(section, "description")}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                      {(section.cards || []).map((f, i) => (
                        <div key={i} className="group flex flex-col gap-4 rounded-2xl border border-ruzy-border bg-white p-8 hover:border-ruzy-accent hover:shadow-xl transition-all">
                          {f.icon && (
                            <div className="bg-ruzy-accent/10 text-ruzy-accent group-hover:bg-ruzy-accent group-hover:text-white rounded-xl p-3 w-fit transition-colors">
                              <span className="material-symbols-outlined text-3xl font-light">{f.icon}</span>
                            </div>
                          )}
                          <div className="flex flex-col gap-2">
                            <h3 className="text-ruzy-dark text-xl font-bold">{ti(f, "title")}</h3>
                            <p className="text-ruzy-text text-sm leading-relaxed">{ti(f, "description")}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </ScrollReveal>
              );
            }
 
                // ===== 3. STATS SECTION =====
             if (section.__component === "sections.stats-section") {
                    return (
                     <ScrollReveal key={idx} delay={0.2}>
                     <section className="mb-20 bg-ruzy-accent/5 rounded-3xl p-8 md:p-12 border border-ruzy-border">
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                       {(section.stats || []).map((s, i) => {
                       const accentColor = s.isRed ? "text-red-500" : "text-ruzy-accent";
                         return (
                       <div key={i} className="flex flex-col gap-2 p-6 bg-white rounded-2xl shadow-sm">
                       {(ti(s, "title") || ti(s, "label")) && (
                       <p className="text-gray-500 text-sm font-semibold uppercase tracking-widest">
                    {ti(s, "title") || ti(s, "label")}
                  </p>
                  )}
                  {s.value && (
                  <p className="text-ruzy-dark text-4xl font-black">{s.value}</p>
                   )}
                   {s.icon && s.subText && (
                  <div className={`flex items-center justify-center gap-1 ${accentColor}`}>
                    <span className="material-symbols-outlined text-lg">{s.icon}</span>
                    <span className="text-sm font-bold">{ti(s, "subText")}</span>
                    </div>
                      )}
                     </div>
                    );
                   })}
                 </div>
               </section>
             </ScrollReveal>
            );
           }
 
            // ===== 4. GRID SECTION =====
            if (section.__component === "sections.grid-section") {
              const gridImg = getStrapiImg(section.image)
              return (
                <ScrollReveal key={idx} delay={0.3}>
                  <section className="mb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div className="flex flex-col gap-10">
                      {t(section, "title") && (
                        <h2 className="text-ruzy-dark text-3xl md:text-5xl font-black tracking-tight leading-tight">
                          {t(section, "title")}
                        </h2>
                      )}
                      <div className="flex flex-col gap-8">
                        {(section.features || []).map((item, i) => (
                          <div key={i} className="group flex items-start gap-6">
                            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-ruzy-accent/10 text-ruzy-accent flex items-center justify-center group-hover:bg-ruzy-accent group-hover:text-white transition-all">
                              <span className="material-symbols-outlined text-3xl font-light">
                                {item.icon || "check_circle"}
                              </span>
                            </div>
                            <div className="flex flex-col gap-2">
                              <h3 className="text-ruzy-dark text-xl font-bold tracking-tight">
                                {ti(item, "title")}
                              </h3>
                              <p className="text-ruzy-text text-base leading-relaxed max-w-md">
                                {ti(item, "description")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="relative">
                      {gridImg && (
                       <img
                          src={gridImg}
                           className="rounded-3xl shadow-2xl border border-ruzy-border"
                          alt="Donanim"
                        />
                      )}
                    </div>
                  </section>
                </ScrollReveal>
              );
            }
 
            return null;
          })}
        </main>
      </div>
    </>
  );
};
 
export default Donanim;